import axios from "axios";
import { motion } from "framer-motion";
import { toast, Bounce } from "react-toastify";
import { FiSend } from "react-icons/fi";
import EmojiPicker from "emoji-picker-react";
import { GrAttachment } from "react-icons/gr";
import { PiSmileyBold } from "react-icons/pi";
import CONSTANTS from "src/components/services/api";
import { MdMic, MdSettingsVoice } from "react-icons/md";
import { IoMicOff } from "react-icons/io5";
import { useState, useRef, useEffect, useContext } from "react";
import {
  AdminDashboardContext,
  MyContext,
} from "src/components/Context/MyContext";
import Cookies from "js-cookie";
const ChatInputField = ({
  openAttachment = () => {},
  inputCss,
  setInputValue = () => {},
  inputValue,
  handleEnter = () => {},
  loading,
  handleUpload,
}) => {
  const { sendTypingOnSignal, sendTypingOffSignal } = useContext(
    AdminDashboardContext
  );
  const { selectedChat } = useContext(MyContext);

  const audioChunks = useRef([]);
  const textareaRef = useRef(null);
  const typingTimer = useRef(null);
  const startTimeRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [time, setTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [typingSent, setTypingSent] = useState(false);
  const [micposition, setMicPosition] = useState("enable");
  const typingTimeoutDuration = 3000;
  const BearerToken = Cookies.get("Token");

  const handleEmojiClick = (emoji) => {
    setInputValue((prev) => prev + emoji.emoji);
    adjustHeight();
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
    if (e.target.value == "") {
      if (typingSent) {
        sendTypingOffSignal();
        setTypingSent(false);
      }
    } else {
      if (!typingSent) {
        sendTypingOnSignal();
        setTypingSent(true);
      }
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
      startTypingTimeout();
    }
    adjustHeight();
  };

  const startTypingTimeout = () => {
    typingTimer.current = setTimeout(() => {
      sendTypingOffSignal();
      setTypingSent(false);
    }, typingTimeoutDuration);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  //this function is used for sending the message
  const handleSendMessage = () => {
    if (inputValue.trim()) {
      handleEnter(inputValue);
      setInputValue("");
      resetTextareaHeight();
      setShowEmojiPicker(false);
    }
  };

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`; // Adjust dynamically
    }
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
    }
  };

  useEffect(() => {
    setInputValue("");
  }, [selectedChat]);

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [isRunning]);

  useEffect(() => {
    textareaRef.current.focus();
    adjustHeight();
  }, [inputValue]);

  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest(".smiley-icon") // Prevent closing when clicking the smiley icon
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);
  const [micStream, setMicStream] = useState(null);
  const [readyToRecord, setReadyToRecord] = useState(false);
  const isHoldingRef = useRef(false); // Track if the user is still holding the mic

  const prepareMicrophone = async () => {
    const micAvailable = await checkMicrophoneAvailability();
    if (!micAvailable) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicStream(stream);
      setReadyToRecord(true);

      // If user is still holding after permission granted
      if (isHoldingRef.current) {
        startRecording(stream);
      }
    } catch (error) {
      console.error("Permission denied or other error:", error);
      setMicPosition("disable");
      toast("Permission denied please enable the permission!", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
    }
  };

  const startRecording = (stream) => {
    startTimeRef.current = Date.now();
    setIsRunning(true);
    setMicPosition("enable");
    const mediaRecorder = new MediaRecorder(stream);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/aac" });
      const endTime = Date.now();
      const timeElapsed = (endTime - startTimeRef.current) / 1000;
      const minutes = Math.floor(timeElapsed / 60);
      const seconds = Math.floor(timeElapsed % 60);
      handleCaptureUpload(audioBlob, `${minutes}.${seconds}`, seconds, minutes);
      audioChunks.current = [];
      stream.getTracks().forEach((track) => track.stop());
      setMicStream(null);
      setReadyToRecord(false);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
  };

  const stopRecording = () => {
    isHoldingRef.current = false;
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRunning(false);
      setTime(0);
    }
  };

  // ************** Checking Mic ***************** *
  const checkMicrophoneAvailability = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMicrophone = devices.some(
        (device) => device.kind === "audioinput"
      );

      if (!hasMicrophone) {
        alert(
          "No microphone found. Please connect a microphone to record voice."
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking microphone availability:", error);
      setMicPosition("disable");
      toast("Unable to access microphone. Please check your device settings.", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      // alert("Unable to access microphone. Please check your device settings.");
      return false;
    }
  };

  const handleCaptureUpload = async (bdata, audioDuration, sec, min) => {
    if (!bdata) return;
    if (sec < 2 && min == 0) return;
    const showChat = JSON.parse(localStorage.getItem("showChat"));
    const newFormData = new FormData();
    newFormData.append("file[]", bdata);
    newFormData.append("file_id", "95345234gsdg395");
    newFormData.append("subscription_id", String(showChat._id));
    try {
      const response = await axios.post(
        `${CONSTANTS.API_BASE}/uploadmultiplechatmediaProd`,
        newFormData,
        {
          headers: {
            "Content-Type": `multipart/form-data`,
            Authorization: BearerToken,
          },
        }
      );
      handleUpload(response.data, audioDuration);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error?.message || "Error to upload the media", {
        position: "top-center",
        autoClose: 1000,
        transition: Bounce,
      });
    } finally {
      // close();
    }
  };

  return (
    <div className="flex items-center justify-between gap-x-7 mt-1">
      <div className="relative flex-1">
        <textarea
          ref={textareaRef}
          rows="1"
          placeholder="Type your message"
          className={`bg-[#F7F7FD] border-none pl-16 w-full focus:outline-none rounded-[20px] p-3 resize-none ${inputCss} `}
          value={inputValue}
          onBlur={() => {
            sendTypingOffSignal();
            setTypingSent(false);
            if (typingTimer.current) {
              clearTimeout(typingTimer.current);
            }
          }}
          onChange={handleChange}
          onInput={adjustHeight}
          onKeyDown={handleKeyDown}
          onClick={() => setShowEmojiPicker(false)}
          style={{ overflowY: "auto", maxHeight: "150px", height: "60px" }}
        />
        <PiSmileyBold
          className="text-[#92929D] font-semibold absolute left-5 top-4 cursor-pointer smiley-icon"
          size={28}
          onClick={() => setShowEmojiPicker((prev) => !prev)}
        />
        {showEmojiPicker && (
          <div
            className="absolute top-[-50vh] left-0 z-10"
            ref={emojiPickerRef}
          >
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}
      </div>

      {!loading ? (
        <GrAttachment
          size={30}
          onClick={openAttachment}
          className="cursor-pointer"
        />
      ) : (
        <img
          src="https://i.gifer.com/ZZ5H.gif"
          alt="Loading..."
          className="w-10 h-10"
        />
      )}
      {time > 0 ? (
        <h2 className="flex items-center gap-2 text-red-600 font-semibold">
          <motion.span
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            <MdMic size={24} />
          </motion.span>
          {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
        </h2>
      ) : null}
      <div className="relative group">
        <div
          className={`rounded-full flex h-[50px] w-[50px] items-center justify-center cursor-pointer bg-[#FBF712]`}
          onMouseDown={() => {
            isHoldingRef.current = true;
            if (!micStream) {
              prepareMicrophone();
            } else if (readyToRecord) {
              startRecording(micStream);
            }
          }}
          onMouseLeave={stopRecording}
          onMouseUp={stopRecording}
        >
          {micposition == "enable" ? (
            <MdSettingsVoice size={24} />
          ) : (
            <IoMicOff size={24} />
          )}
        </div>

        {/* Tooltip */}
        <span className="absolute  bottom-12 right-0 -inset-x-36  opacity-0 group-hover:opacity-100 bg-black text-white text-xs rounded-md px-2 py-1 transition-opacity duration-200">
          Hold the icon for voice Recording
        </span>
      </div>

      {inputValue.trim() && (
        <div
          className="bg-[#FBF712] rounded-full p-3 flex items-center justify-center cursor-pointer"
          onClick={handleSendMessage}
        >
          <FiSend size={26} />
        </div>
      )}
    </div>
  );
};

export default ChatInputField;
